import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
} from 'react-share';
import {useClipboard} from 'src/hooks/useClipboard';
import {
  Tooltip,
  Button,
  toastApi,
  Toast,
  ToastTitle,
  SimpleButtonDropdown,
  ButtonTextSmall,
} from '@spaced-out/ui-design-system/lib/components/index';
import css from './configure.css';


type Props = {
  facebookLink?: string;
  linkedinLink?: string;
  twitterLink?: string;
  emailLink?: string;
  messagingLink?: string;
  programName?: string;
  copyLinkText?: string;
  onTrackAnalytics: (social: string, link: string) => unknown;
};

const SocialShare = ({
  facebookLink,
  linkedinLink,
  twitterLink,
  emailLink,
  messagingLink,
  programName = 'Default Program',
  copyLinkText,
  onTrackAnalytics,
}: Props) => {
  const {t} = useLabelTranslation();
  const [_hasCopiedLink, handleLinkCopy] = useClipboard(copyLinkText || '');
  const [_hasCopiedEmailLink, handleEmailLinkCopy] = useClipboard(
    emailLink || '',
  );
  const [_hasCopiedMessagingLink, handleMessagingLinkCopy] = useClipboard(
    messagingLink || '',
  );

  const postDescription = programName
    ? `${t('Participate_in', 'Participate in')} ${programName}, ${t(
        'refer_your_friends_and_acquaintances_for_our_open_job_positions_and_earn_a_referral_bonus',
        'refer your friends and acquaintances for our open job positions and earn a referral bonus',
      )}. ${t('To_know_more', 'To know more')}:`
    : `${t(
        'Refer_your_friends_and_acquaintances_for_our_open_job_positions',
        'Refer your friends and acquaintances for our open job positions',
      )}. ${t('To_know_more', 'To know more')}:`;

  const showToast = (message: string) => {
    toastApi.show(
      <Toast semantic={'success'} initialFocus={0}>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
    );
  };

  const renderShareButton = (
    social: string,
    url: string,
    iconName: string,
    ShareButtonComponent: any,
  ) => {
    const buttonText = social;
    return (
      <ShareButtonComponent
        title={postDescription}
        hashtags={['senseHq']}
        url={url}
      >
        <Tooltip
          elevation="modal"
          body={`${t('Redirect_to', 'Redirect to')} ${social}`}
        >
          <Button
            classNames={{wrapper: css.shareButton}}
            iconLeftName={iconName}
            iconLeftType="brands"
            size="small"
            type="tertiary"
            onClick={() => onTrackAnalytics(social, url)}
          >
            {buttonText}
          </Button>
        </Tooltip>
      </ShareButtonComponent>
    );
  };

  return (
    <div className={css.shareButtonContainer}>
      {!!linkedinLink &&
        renderShareButton(
          t('Linkedin', 'Linkedin'),
          linkedinLink,
          'linkedin',
          LinkedinShareButton,
        )}
      {!!facebookLink &&
        renderShareButton(
          t('Facebook', 'Facebook'),
          facebookLink,
          'facebook',
          FacebookShareButton,
        )}
      {!!twitterLink &&
        renderShareButton(
          t('Twitter', 'Twitter'),
          twitterLink,
          'x-twitter',
          TwitterShareButton,
        )}

      {!!messagingLink && (
        <Tooltip
          elevation="modal"
          body={t('Copy_Messaging_Link', 'Copy Messaging Link')}
        >
          <Button
            classNames={{wrapper: css.shareButton}}
            iconLeftName="message"
            size="small"
            type="tertiary"
            onClick={() => {
              handleMessagingLinkCopy();
              onTrackAnalytics('Messaging', messagingLink);
              showToast(
                t(
                  'Messaging_Link_copied_to_clipboard',
                  'Messaging Link copied to clipboard',
                ),
              );
            }}
          >
            {t('Messaging', 'Messaging')}
          </Button>
        </Tooltip>
      )}

      {!!emailLink && (
        <Tooltip
          elevation="modal"
          body={t('Copy_Email_Link', 'Copy Email Link')}
        >
          <Button
            classNames={{wrapper: css.shareButton}}
            iconLeftName="copy"
            size="small"
            type="tertiary"
            onClick={() => {
              handleEmailLinkCopy();
              onTrackAnalytics('Email', emailLink);
              showToast(
                t(
                  'Email_Link_copied_to_clipboard',
                  'Email Link copied to clipboard',
                ),
              );
            }}
          >
            {t('Email_Link', 'Email Link')}
          </Button>
        </Tooltip>
      )}

      {!!copyLinkText && (
        <SimpleButtonDropdown
          iconLeftName="link"
          size="small"
          type="tertiary"
          classNames={{buttonWrapper: css.shareButton}}
          options={[
            {
              customComponent: <LinkOptionLabel />,
              iconLeft: 'copy',
              key: 'copy_link',
            },
          ]}
          onOptionSelect={() => {
            onTrackAnalytics('Copy Link', copyLinkText);
            handleLinkCopy();
            showToast(
              t('Link_copied_to_clipboard', 'Link copied to clipboard'),
            );
          }}
        >
          {t('Others', 'Others')}
        </SimpleButtonDropdown>
      )}
    </div>
  );
};

export default SocialShare;

const LinkOptionLabel = () => {
  const {t} = useLabelTranslation();
  return (
    <Tooltip
      elevation="modal"
      body={t('Copy_Referral_Link', 'Copy Referral Link')}
    >
      <ButtonTextSmall>{t('Referral_Link', 'Referral Link')}</ButtonTextSmall>
    </Tooltip>
  );
};
